import type { ReactNode } from "react";
import type { Emitter } from "nanoevents";
import { createNanoEvents } from "nanoevents";
import { action, computed, makeObservable, observable, toJS } from "mobx";
import {
  arrDeleteAt,
  arrInsertAt,
  arrReplaceAt,
  removeItem,
  scrollIntoViewHorizontally,
} from "@exonn/utils";

export type TabOptions = {
  id: TabId;
  title?: ReactNode;
  titleText?: string;
  favicon?: ReactNode;
  content?: ReactNode;
  onClose?: () => void | boolean;
  isLoading?: boolean;
  maxWidth?: number;
  minWidth?: number;
  isClosable?: boolean;
  hasUnsavedChanges?: boolean;
  isDisabled?: boolean;
  disabledTooltip?: ReactNode;
  isHidden?: boolean;
  showIndicator?: boolean;
};

export type TabId = string;

interface TabEvents {}

export class Tab {
  emitter: Emitter<TabEvents>;

  @observable
  id: TabId;

  title: ReactNode;

  titleText: string;

  favicon: ReactNode;

  content: ReactNode;

  onClose?: () => void | boolean;

  @observable
  isLoading: boolean;

  @observable
  isClosable: boolean;

  @observable
  isDisabled: boolean;

  @observable
  disabledTooltip: ReactNode;

  @observable
  isHidden: boolean;

  @observable
  maxWidth: number | undefined;

  @observable
  minWidth: number | undefined;

  @observable
  showIndicator: boolean;

  domEl: HTMLElement | null = null;

  @computed
  get options(): TabOptions {
    return toJS({
      id: this.id,
      title: this.title,
      favicon: this.favicon,
      maxWidth: this.maxWidth,
      titleText: this.titleText,
      disabled: this.disabledTooltip,
      minWidth: this.minWidth,
      content: this.content,
      isClosable: this.isClosable,
      isLoading: this.isLoading,
      hasUnsavedChanges: this.hasUnsavedChanges,
      isDisabled: this.isDisabled,
    });
  }

  @observable
  hasUnsavedChanges: boolean;

  store: TabsStore;

  constructor(store: TabsStore, props: TabOptions) {
    const {
      id,
      isLoading = false,
      hasUnsavedChanges = false,
      isDisabled = false,
      isClosable = true,
      disabledTooltip,
      titleText = "",
      onClose,
      isHidden = false,
      maxWidth,
      minWidth,
      showIndicator = false,
      title,
      favicon = "",
      content = "",
    } = props;
    this.store = store;
    this.id = id;
    this.title = title;
    this.favicon = favicon;
    this.content = content;
    this.titleText = titleText;
    this.disabledTooltip = disabledTooltip;
    this.isLoading = isLoading;
    this.maxWidth = maxWidth;
    this.minWidth = minWidth;
    this.onClose = onClose;
    this.hasUnsavedChanges = hasUnsavedChanges;
    this.isClosable = isClosable;
    this.isDisabled = isDisabled;
    this.isHidden = isHidden;
    this.showIndicator = showIndicator;
    this.emitter = createNanoEvents<TabEvents>();
    makeObservable(this);
  }

  @action
  close(emitEvents = true) {
    return this.store.setTabs(removeItem(this.store.tabs, this), {
      emitEvents,
    });
  }

  setDomEl(domEl: HTMLElement | null) {
    this.domEl = domEl;
  }

  scrollIntoView() {
    const container = this.store.containerEl;
    const tabElement = this.domEl;
    if (container && tabElement) {
      scrollIntoViewHorizontally(container, tabElement);
    }
  }

  @action
  move(indexAt: number, emitEvents = true) {
    this.store.setTabs(
      arrInsertAt(
        arrDeleteAt(this.store.tabs, this.index),
        indexAt,
        this.store.tabs[this.index],
      ),
      {
        emitEvents,
      },
    );
  }

  @action
  pin({
    emitEvents = true,
  }: {
    emitEvents?: boolean;
  } = {}) {
    if (!this.isPinned) {
      this.move(this.store.pinnedLeft.size);
      const prevPinned = [...this.store.pinnedLeft];
      this.store.setPinnedLeft(
        [...prevPinned.filter((id) => id !== this.id), this.id],
        {
          emitEvents,
        },
      );
    }
  }

  @action
  unpin({
    emitEvents = true,
  }: {
    emitEvents?: boolean;
  } = {}) {
    if (this.isPinned) {
      this.move(this.store.pinnedLeft.size - 1, emitEvents);
      const prevPinned = [...this.store.pinnedLeft];
      this.store.setPinnedLeft(
        prevPinned.filter((id) => id !== this.id),
        {
          emitEvents,
        },
      );
    }
  }

  @action
  setLoading(isLoading: boolean) {
    this.isLoading = isLoading;
  }

  @action
  setIsOutOfView(isOutOfView: boolean) {
    if (isOutOfView) {
      this.store.setOutOfViewTabIds([
        ...this.store.outOfViewTabIds.filter((tab) => tab !== this.id),
        this.id,
      ]);
    } else {
      this.store.setOutOfViewTabIds(
        removeItem(this.store.outOfViewTabIds, this.id),
      );
    }
  }

  @computed
  get isOutOfView() {
    return this.store.outOfViewTabIds.includes(this.id);
  }

  @action
  setDisabled(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  @action
  setIsHidden(isHidden: boolean) {
    this.isHidden = isHidden;
  }

  @action
  setShowIndicator = (isShow: boolean) => {
    this.showIndicator = isShow;
  };

  @computed
  get index() {
    return this.store.tabs.findIndex((tab) => tab.id === this.id);
  }

  @computed
  get isFirst() {
    return this.index === 0;
  }

  @computed
  get isPinned() {
    return this.store.pinnedLeft.has(this.id);
  }

  @computed
  get isNotPinned() {
    return !this.isPinned;
  }

  @computed
  get isDragged() {
    return this.id === this.store.draggedTabId;
  }

  @computed
  get isHovered() {
    return this.id === this.store.hoveredTabId;
  }

  @computed
  get isActive() {
    return this.id === this.store.activeTabId;
  }

  @computed
  get isLast() {
    return this.id === this.store.tabs[this.store.tabs.length - 1].id;
  }

  @computed
  get previous(): Tab | undefined {
    return this.store.tabs[this.index - 1];
  }

  @computed
  get next(): Tab | undefined {
    return this.store.tabs[this.index + 1];
  }

  @computed
  get closestTab(): Tab | undefined {
    return this.next || this.previous;
  }

  is(tab: Tab | undefined) {
    return this.id === tab?.id;
  }

  @action
  startDragging() {
    this.store.draggedTabId = this.id;
  }

  @action
  stopDragging() {
    this.store.draggedTabId = undefined;
  }

  @action
  activate(
    params: {
      emitEvents?: boolean;
      updateState?: boolean;
    } = {},
  ) {
    this.store.setActiveTabId(this.id, params);
  }

  @action.bound
  setHasUnsavedChanges(hasUnsavedChanges: boolean) {
    this.hasUnsavedChanges = hasUnsavedChanges;
  }
}

interface TabStoreEvents {
  tabsSet: (nextTabs: Tab[], closedTabs: Tab[]) => void;
  pinnedLeftChange: (pinnedLeft: string[]) => void;
  activeTabChange: (tab: Tab | undefined, currentTab: Tab | undefined) => void;
}

export class TabsStore {
  emitter: Emitter<TabStoreEvents>;

  hasControlledTabId: boolean = false;

  @observable
  tabs: Tab[] = [];

  containerEl: HTMLElement | null = null;

  @observable
  scrollAreaEndReached: boolean = true;

  @observable
  scrollAreaStartReached: boolean = true;

  @observable
  activeTabAutoComebackEnabled: boolean = true;

  @observable
  draggedTabId: TabId | undefined = undefined;

  @observable
  activeTabId: TabId | undefined = undefined;

  @observable
  hoveredTabId: TabId | undefined = undefined;

  @observable
  pinnedLeft: Set<TabId> = new Set();

  @observable
  outOfViewTabIds: string[] = [];

  constructor() {
    this.emitter = createNanoEvents<TabStoreEvents>();

    makeObservable(this);
  }

  @computed
  get unpinnedTabs() {
    return this.tabs.filter((tab) => !this.pinnedLeft.has(tab.id));
  }

  setContainerEl(containerEl: HTMLElement | null) {
    this.containerEl = containerEl;
  }

  setHasControlledTabId(state: boolean) {
    this.hasControlledTabId = state;
  }

  @action
  setPinnedLeft(
    ids: string[],
    {
      emitEvents = true,
    }: {
      emitEvents?: boolean;
    } = {},
  ) {
    this.pinnedLeft.clear();
    if (emitEvents) {
      this.emitter.emit("pinnedLeftChange", ids);
    }

    ids.forEach((id) => {
      this.pinnedLeft.add(id);
    });
  }

  @computed
  get pinnedTabs() {
    return this.tabs.filter((tab) => this.pinnedLeft.has(tab.id));
  }

  @computed
  get firstTab() {
    return this.tabs?.[0];
  }

  @computed
  get lastTab() {
    return this.tabs[this.tabs.length - 1];
  }

  @computed
  get hoveredTab() {
    return this.hoveredTabId ? this.getTab(this.hoveredTabId) : undefined;
  }

  @computed
  get activeTab() {
    return this.activeTabId ? this.getTab(this.activeTabId) : undefined;
  }

  @computed
  get draggedTab() {
    return this.draggedTabId ? this.getTab(this.draggedTabId) : undefined;
  }

  @action
  updateTab(tab: Tab) {
    this.setTabs(arrReplaceAt(this.tabs, tab.index, tab));
  }

  @action
  setOutOfViewTabIds(ids: TabId[]): void {
    this.outOfViewTabIds = ids;
  }

  @action
  setTabs(
    nextTabs: Tab[],
    {
      emitEvents = true,
    }: {
      emitEvents?: boolean;
    } = {},
  ) {
    const closedTabs = asymmetricDiffTabs(this.tabs, nextTabs);
    let setTabsPrevented = false;
    closedTabs.forEach((tab) => {
      const closedPrevented = tab.onClose?.();
      if (closedPrevented) {
        setTabsPrevented = true;
      }
      if (setTabsPrevented) {
        return;
      }
      tab.unpin();
      if (tab.isActive) {
        const closestTab = tab.closestTab;
        const closestTabExists = nextTabs.find((tab) => tab.is(closestTab));

        if (!closestTabExists) {
          this.setActiveTabId(nextTabs?.[nextTabs.length - 1]?.id);
        } else {
          closestTab?.activate();
        }
      }
    });

    if (setTabsPrevented) {
      return;
    }

    if (emitEvents) {
      this.emitter.emit("tabsSet", nextTabs, closedTabs);
    }

    this.tabs = nextTabs.filter((tab) => !tab.isHidden);
  }

  @action
  setDraggedTabId(id: TabId | undefined) {
    this.draggedTabId = id;
  }

  @action
  setHoveredTabId(id: TabId | undefined) {
    this.hoveredTabId = id;
  }

  @action
  setActiveTabId(
    id: TabId | undefined,
    {
      emitEvents = true,
      updateState = true,
    }: {
      emitEvents?: boolean;
      updateState?: boolean;
    } = {},
  ) {
    const tab = this.tabs.find((tab) => tab.id === id);

    if (emitEvents) {
      this.emitter.emit("activeTabChange", tab, this.activeTab);
    }

    if (updateState) {
      this.activeTabId = id;
    }
  }

  @action
  setActiveTabAutoComebackEnabled(state: boolean) {
    this.activeTabAutoComebackEnabled = state;
  }

  @action
  setScrollAreaStartReached(reached: boolean) {
    this.scrollAreaStartReached = reached;
  }

  @action
  setScrollAreaEndReached(reached: boolean) {
    this.scrollAreaEndReached = reached;
  }

  getTab(id: TabId) {
    return this.tabs.find((tab) => tab.id === id);
  }

  @action.bound
  createTab(props: TabOptions) {
    return new Tab(this, props);
  }

  @action
  prependTab(props: TabOptions) {
    const tab = this.createTab(props);
    this.setTabs(arrInsertAt(this.tabs, this.pinnedLeft.size, tab));

    return tab;
  }

  @action
  prependTabs(tabs: TabOptions[]) {
    return tabs.map((tab) => {
      return this.prependTab(tab);
    });
  }

  @action
  appendTab(props: TabOptions) {
    const tab = this.createTab(props);
    this.setTabs([...this.tabs, tab]);

    return tab;
  }

  @action
  insertTabAfter(tab: Tab, nextTab: Tab) {
    this.setTabs(arrInsertAt(this.tabs, tab.index + 1, nextTab));
  }

  @computed
  get hasTabs() {
    return !!this.tabs.length;
  }

  @computed
  get outOfViewTabs() {
    return this.tabs.filter((tab) => this.outOfViewTabIds.includes(tab.id));
  }

  @computed
  get inViewTabs() {
    return this.tabs.filter((tab) => !this.outOfViewTabIds.includes(tab.id));
  }
}

const asymmetricDiffTabs = (tabs: Tab[], tabs2: Tab[]): Tab[] => {
  return tabs.filter((tab) => !tabs2.map((tab2) => tab2.id).includes(tab.id));
};
